import { Injectable } from '@angular/core';

export interface RawSeat {
  /** TID*/
  rstu: string;
  /** Red) */
  row?: string | number;
  /** Broj sjedala u redu */
  seat?: number;
  isFree: boolean;
}

interface SeatModel {
  id:    string;
  row:   number;
  num:   number;
  isFree: boolean;
}

@Injectable({ providedIn: 'root' })
export class SeatValidationService {
  /** 
   * Pretvori sirovi objekt (RawSeat) u uniformni model sa brojem reda i mjesta. 
   * Ako row/seat nisu definirani, pokuša ih parsirati iz rstu stringa. 
   */
  private toModel(s: RawSeat): SeatModel | null {
    let rowNum: number | undefined;
    let seatNum: number | undefined;

    // 1) Ako DTO već ima row i seat, uzmi ih:
    if (s.row != null && s.seat != null) {
      rowNum  = +s.row;
      seatNum = +s.seat;
    } else {
      // 2) Inače pokuša parsirati iz ID-ja: r<row>s<seat>
      //    npr. "r1s32..." → row=1, seat=32
      const m = s.rstu.match(/r(\d+)s(\d+)/i);
      if (m) {
        rowNum  = parseInt(m[1], 10);
        seatNum = parseInt(m[2], 10);
      }
    }

    if (rowNum == null || seatNum == null) {
      // ne možemo interpretirati ovo sjedalo → ignoriramo ga
      return null;
    }

    return {
      id:    s.rstu,
      row:   rowNum,
      num:   seatNum,
      isFree: s.isFree
    };
  }

  /**
   * Glavna metoda: vraća false čim nađe izolirano slobodno mjesto
   * između odabranih sjedala.
   */
  validateSeatSelection(
    rawSelected: RawSeat[] | null | undefined,
    rawAll:      RawSeat[] | null | undefined
  ): boolean {
    // Ako nema selektiranih ili ne postoje podaci, sve je valjano
    if (!Array.isArray(rawSelected) || rawSelected.length === 0) {
      return true;
    }
    if (!Array.isArray(rawAll) || rawAll.length === 0) {
      return true;
    }

    // Pretvori u uniformne modele i izbaci ne-parse-ana
    const selected = rawSelected
      .map(s => this.toModel(s))
      .filter((x): x is SeatModel => !!x);
    const all = rawAll
      .map(s => this.toModel(s))
      .filter((x): x is SeatModel => !!x);

    // Set ID‐eva za brzu provjeru „je li netko označen“
    const selIds = new Set(selected.map(s => s.id));

    // Redovi u kojima imamo barem jednu selekciju
    const rows = Array.from(new Set(selected.map(s => s.row)));

    for (const row of rows) {
      // Sva sjedala u tom redu
      const rowAll  = all.filter(s => s.row === row);
      // Slobodna u tom redu
      const rowFree = rowAll.filter(s => s.isFree);
      // Selektirano u tom redu
      const rowSel  = selected.filter(s => s.row === row);

      // Pravilo: ako je samo jedno slobodno mjesto ostalo,
      //          preskoči validaciju za taj red
      if (rowFree.length - rowSel.length === 1) {
        continue;
      }

      // Sortiraj označena sjedala po broju
      rowSel.sort((a, b) => a.num - b.num);

      // Napravi klastere uzastopnih sjedala
      const clusters: SeatModel[][] = [];
      let cluster: SeatModel[] = [];
      for (const s of rowSel) {
        if (
          cluster.length > 0 &&
          s.num === cluster[cluster.length - 1].num + 1
        ) {
          cluster.push(s);
        } else {
          if (cluster.length) {
            clusters.push(cluster);
          }
          cluster = [s];
        }
      }
      if (cluster.length) {
        clusters.push(cluster);
      }

      // Pomagač za dohvat “susjednih” sjedala
      const find = (n: number) =>
        rowAll.find(x => x.num === n) || null;

      // Sada provjeri svaku grupu
      for (const cl of clusters) {
        const first = cl[0].num;
        const last  = cl[cl.length - 1].num;

        let prev1 = find(first - 1);
        let prev2 = find(first - 2);
        let next1 = find(last  + 1);
        let next2 = find(last  + 2);

        // Ako nije slobodno, tretiraj kao da ga nema
        if (prev1 && !prev1.isFree) { prev1 = null; prev2 = null; }
        if (prev2 && !prev2.isFree) { prev2 = null; }
        if (next1 && !next1.isFree) { next1 = null; next2 = null; }
        if (next2 && !next2.isFree) { next2 = null; }

        // A) izolirano slobodno sjedalo između 2 selektirana (prije klastera)
        if (
          prev2 && selIds.has(prev2.id) &&
          prev1 && prev1.isFree
        ) {
          return false;
        }
        // B) samo jedno slobodno mjesto na početku klastera + još mjesto iza
        if (
          !prev2 &&
          prev1 && prev1.isFree &&
          next1
        ) {
          return false;
        }
        // C) izolirano slobodno sjedalo između 2 selektirana (iza klastera)
        if (
          next2 && selIds.has(next2.id) &&
          next1 && next1.isFree
        ) {
          return false;
        }
        // D) samo jedno slobodno mjesto na kraju klastera + još mjesto prije
        if (
          !next2 &&
          next1 && next1.isFree &&
          prev1
        ) {
          return false;
        }
      }
    }

    // Ako nismo ništa našli – OK!
    return true;
  }
}
